import { API_URL } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { useOrganization } from '@/hooks/useOrganization';

interface UserPermissions {
  isAdmin: boolean;
  permissions: string[];
}

export function usePermissions() {
  const { organization, isLoading: isLoadingOrg } = useOrganization();

  const { data, isLoading, error } = useQuery<UserPermissions>({
    queryKey: ['permissions', organization?.id],
    queryFn: async () => {
      if (!organization?.id) {
        throw new Error('No organization selected');
      }

      const response = await fetch(
        `${API_URL}/organizations/${organization.id}/users/me/permissions`,
        {
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch permissions');
      }

      return response.json();
    },
    enabled: !!organization?.id,
  });

  const hasPermission = (permissionKey: string): boolean => {
    if (!data) return false;
    if (data.isAdmin) return true;
    return data.permissions.includes(permissionKey);
  };

  const hasAnyPermission = (permissionKeys: string[]): boolean => {
    if (!data) return false;
    if (data.isAdmin) return true;
    return permissionKeys.some(key => data.permissions.includes(key));
  };

  const hasAllPermissions = (permissionKeys: string[]): boolean => {
    if (!data) return false;
    if (data.isAdmin) return true;
    return permissionKeys.every(key => data.permissions.includes(key));
  };

  return {
    permissions: data?.permissions || [],
    isAdmin: data?.isAdmin || false,
    isLoading: isLoading || isLoadingOrg,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };
}
